// Centralized SEO utilities: site constants and runtime meta-tag management.

const rawSiteUrl = import.meta.env.VITE_SITE_URL as string | undefined;

export const SITE_URL =
  typeof rawSiteUrl === 'string' && rawSiteUrl.trim()
    ? rawSiteUrl.trim().replace(/\/+$/, '')
    : 'https://cssbuybr.hair';

export const FAVICON_URL =
  'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/d367cf6745ee47f3fc82c76d3a54c50c.png';

const HOME_OG_IMAGE =
  'https://readdy.ai/api/search-image?query=Artistic%20abstract%20fashion%20collage%20background%20with%20flowing%20fabric%20and%20warm%20terracotta%20and%20cream%20tones%2C%20soft%20light%20gradients%2C%20premium%20editorial%20aesthetic%2C%20elegant%20minimal%20composition%2C%20high%20end%20lifestyle%20mood&width=1600&height=900&seq=hero-main-02&orientation=landscape&nocache=true';

export interface SeoMeta {
  title: string;
  description: string;
  keywords?: string;
  canonical: string;
  ogType?: string;
  ogImage?: string;
}

function upsertMeta(attr: 'name' | 'property', key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertLink(rel: string, href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

export function applySeoMeta(meta: SeoMeta) {
  document.title = meta.title;
  upsertMeta('name', 'description', meta.description);
  if (meta.keywords) {
    upsertMeta('name', 'keywords', meta.keywords);
  }
  upsertLink('canonical', meta.canonical);
  upsertMeta('name', 'robots', 'index, follow');
  upsertMeta('property', 'og:title', meta.title);
  upsertMeta('property', 'og:description', meta.description);
  upsertMeta('property', 'og:type', meta.ogType ?? 'website');
  upsertMeta('property', 'og:url', meta.canonical);
  if (meta.ogImage) {
    upsertMeta('property', 'og:image', meta.ogImage);
  }
  upsertMeta('name', 'twitter:card', 'summary_large_image');
  upsertMeta('name', 'twitter:title', meta.title);
  upsertMeta('name', 'twitter:description', meta.description);
  if (meta.ogImage) {
    upsertMeta('name', 'twitter:image', meta.ogImage);
  }
}

export function applyNoIndex(title?: string) {
  if (title) {
    document.title = title;
  }
  upsertMeta('name', 'robots', 'noindex, nofollow');
}

export function getHomeMeta(): SeoMeta {
  return {
    title: 'CSSBuy Spreadsheet – Products, Guides & Resources for China Shopping',
    description:
      'Explore CSSBuy Spreadsheet – an independent resource with organized product categories, buying guides, QC tips, shipping info, and safety advice for China shopping research.',
    keywords:
      'CSSBuy Spreadsheet, CSSBuy guide, China shopping, buying agent, product research, QC guide, shipping guide',
    canonical: `${SITE_URL}/`,
    ogType: 'website',
    ogImage: HOME_OG_IMAGE,
  };
}

export function getArticleMeta(
  title: string,
  description: string,
  slug: string,
  image: string,
): SeoMeta {
  return {
    title,
    description,
    keywords: `CSSBuy Spreadsheet, CSSBuy guide, ${title}`,
    canonical: `${SITE_URL}/article/${slug}`,
    ogType: 'article',
    ogImage: image,
  };
}